/**
 * Pending booking selection — survives the sign-in redirect.
 *
 * A signed-out visitor picks dates/times on the public space detail page and
 * then has to authenticate to confirm. Full-page navigation through /signin
 * destroys React state, so the selection is parked here and handed back to the
 * detail page after authentication. This is the web equivalent of mobile's
 * `pendingSpaceNavigationProvider`.
 *
 * Deliberately persisted to localStorage rather than held in memory: the sign-in
 * journey is a hard navigation, and a user may leave the tab open, so in-memory
 * state is not sufficient.
 */

const STORAGE_KEY = "spacia_pending_booking_selection";

/** The date/time window the visitor chose, serialised to ISO strings. */
export interface PendingBookingWindow {
  /** ISO timestamp. */
  startTime: string;
  /** ISO timestamp. */
  endTime: string;
  /** ISO date (yyyy-mm-dd) or null when the picker did not emit one. */
  startDate?: string | null;
  endDate?: string | null;
  /** Human label such as "2 hours" / "3 days". */
  durationLabel?: string;
}

export interface PendingBookingAddon {
  addonId: number;
  quantity: number;
}

export interface PendingBookingSelection {
  /** Space the selection belongs to; guards against restoring onto the wrong space. */
  spaceId: number;
  /** Path to return to after sign-in, e.g. "/space/123". */
  returnPath: string;
  window: PendingBookingWindow;
  quantity?: number;
  notes?: string;
  addons?: PendingBookingAddon[];
  /** Attendee emails, when the space collects them. */
  emails?: string[];
  /** Epoch ms, used to discard stale drafts. */
  createdAt: number;
}

/** Drafts older than this are ignored so a user is never resumed into a stale date. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const hasWindow = (value: unknown): value is PendingBookingWindow => {
  if (!value || typeof value !== "object") return false;
  const w = value as Partial<PendingBookingWindow>;
  return typeof w.startTime === "string" && typeof w.endTime === "string" && !!w.startTime && !!w.endTime;
};

const isValidEnd = (window: PendingBookingWindow): boolean => {
  const start = new Date(window.startTime).getTime();
  const end = new Date(window.endTime).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return end > start;
};

/**
 * Persist a selection so it can be restored after authentication.
 * No-ops when the window is missing or inverted, so we never resume a
 * booking the app itself would have rejected.
 */
export const savePendingBookingSelection = (
  payload: Omit<PendingBookingSelection, "createdAt">
): boolean => {
  if (!hasWindow(payload.window) || !isValidEnd(payload.window)) {
    clearPendingBookingSelection();
    return false;
  }

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...payload, createdAt: Date.now() })
    );
    return true;
  } catch {
    // Storage can be unavailable (private mode, quota). Losing the draft is
    // preferable to blocking the sign-in flow.
    return false;
  }
};

/** Read the stored draft, or null when absent, malformed, or expired. */
export const readPendingBookingSelection = (): PendingBookingSelection | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingBookingSelection>;
    if (typeof parsed?.spaceId !== "number" || !parsed.returnPath) {
      clearPendingBookingSelection();
      return null;
    }
    if (!hasWindow(parsed.window) || !isValidEnd(parsed.window)) {
      clearPendingBookingSelection();
      return null;
    }
    if (
      typeof parsed.createdAt === "number" &&
      Date.now() - parsed.createdAt > MAX_AGE_MS
    ) {
      clearPendingBookingSelection();
      return null;
    }

    return parsed as PendingBookingSelection;
  } catch {
    clearPendingBookingSelection();
    return null;
  }
};

/**
 * Read the stored draft only when it belongs to the given space.
 * Returns null for a different space so we never apply a draft to the wrong
 * listing, and clears the stale entry in that case.
 */
export const readPendingBookingSelectionForSpace = (spaceId: number): PendingBookingSelection | null => {
  const pending = readPendingBookingSelection();
  if (!pending) return null;

  if (pending.spaceId !== spaceId) {
    clearPendingBookingSelection();
    return null;
  }
  return pending;
};

export const clearPendingBookingSelection = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the draft will be treated as absent.
  }
};
